import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, TrendingUp, DollarSign } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const AmmoStatistics = () => {
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState({
    sales_by_caliber: [],
    current_stock: {},
    total_sales: 0
  });

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/admin/ammo-statistics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatistics(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching ammo statistics:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <p className="text-gray-400">Laster ammo-statistikk...</p>
      </div>
    );
  }

  if (statistics.sales_by_caliber.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <img src="/patron-symbol.svg" alt="Ammo" className="h-6 w-6" />
          <h3 className="text-xl font-bold text-white">Ammunisjonsstatistikk</h3>
        </div>
        <p className="text-gray-400 text-center py-8">Ingen ammo-salg registrert ennå</p>
      </div>
    );
  }

  const totalRevenue = statistics.sales_by_caliber.reduce((sum, s) => sum + s.total_revenue, 0);
  const totalBoxes = statistics.sales_by_caliber.reduce((sum, s) => sum + s.total_boxes_sold, 0);
  const totalRounds = statistics.sales_by_caliber.reduce((sum, s) => sum + s.total_rounds_sold, 0);

  // Sort by revenue
  const sortedByRevenue = [...statistics.sales_by_caliber].sort((a, b) => b.total_revenue - a.total_revenue);

  // Find max for scaling bars
  const maxRevenue = Math.max(...statistics.sales_by_caliber.map(s => s.total_revenue));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <img src="/patron-symbol.svg" alt="Ammo" className="h-6 w-6" />
          <h3 className="text-xl font-bold text-white">Ammunisjonsstatistikk</h3>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-gray-700 rounded-lg">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <Package className="h-4 w-4" />
              <span>Totalt omsetning</span>
            </div>
            <p className="text-2xl font-bold text-yellow-400">{totalRevenue.toFixed(2)} kr</p>
          </div>
          <div className="p-4 bg-gray-700 rounded-lg">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <TrendingUp className="h-4 w-4" />
              <span>Totale transaksjoner</span>
            </div>
            <p className="text-2xl font-bold text-white">{statistics.total_sales}</p>
          </div>
          <div className="p-4 bg-gray-700 rounded-lg">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <Package className="h-4 w-4" />
              <span>Totalt solgt</span>
            </div>
            <p className="text-2xl font-bold text-white">{totalBoxes} esker</p>
            <p className="text-sm text-gray-400 mt-1">{totalRounds.toLocaleString()} skudd</p>
          </div>
        </div>

        {/* Sales by Caliber */}
        <div>
          <h4 className="text-white font-semibold mb-4">Salg per ammunisjonstype</h4>
          <div className="space-y-3">
            {sortedByRevenue.map((caliber) => (
              <div key={caliber.caliber} className="bg-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="text-white font-medium text-lg">{caliber.caliber}</h5>
                  <span className="text-yellow-400 font-bold">{caliber.total_revenue.toFixed(2)} kr</span>
                </div>
                
                <div className="grid grid-cols-3 gap-4 mb-3">
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Esker solgt</p>
                    <p className="text-white font-semibold">{caliber.total_boxes_sold} stk</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Patroner solgt</p>
                    <p className="text-white font-semibold">{caliber.total_rounds_sold.toLocaleString()} stk</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Gjennomsnittspris</p>
                    <p className="text-white font-semibold">
                      {(caliber.total_revenue / caliber.total_boxes_sold).toFixed(2)} kr/eske
                    </p>
                  </div>
                </div>
                
                {/* Progress bar showing relative revenue */}
                <div className="relative h-2 bg-gray-600 rounded-full overflow-hidden">
                  <div 
                    className="absolute h-full bg-yellow-400 rounded-full transition-all duration-500"
                    style={{ width: `${(caliber.total_revenue / maxRevenue) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Current Stock Overview */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h4 className="text-white font-semibold mb-4">Gjeldende lagerstatus</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(statistics.current_stock).map(([caliber, stock]) => (
            <div key={caliber} className="p-4 bg-gray-700 rounded-lg">
              <h5 className="text-white font-medium mb-2">{caliber}</h5>
              <p className="text-2xl font-bold text-yellow-400">{stock} esker</p>
              <p className="text-sm text-gray-400 mt-1">på lager</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AmmoStatistics;
